// Region: Class definition *********************************************************************/
/**
 * Parses messages sent from the server, through signalR.
 */
window.messages = new class {

    // #region: Private Fields *******************************************************************/
    #actions = [];
    // #endregion ********************************************************************************/

    // Region: Constructor **********************************************************************/
    constructor() {

        // Timeout/retry constants:
        this.TIMEOUT_3_MINUTES = 380000;
        this.TIMEOUT_6_MINUTES = 360000;

        this.RETRY_05_SECONDS = 5000;
        this.RETRY_15_SECONDS = 15000;
        this.RETRY_50_SECONDS = 50000;
    }
    // endregion ********************************************************************************/

    // region: Build ****************************************************************************/
    /**
     * Identifies class.
     * @returns {string}                        The string representation of this class.
     */
    toString() {
        return "[messages]";
    }

    static toString() {
        return "[messages]";
    }

    /** 
     * Identifies the version.
     * @returns {string}                        The version number of this class.
     */
    static version() {
        return "1.0.0";
    }
    // endregion ********************************************************************************/

    // region: Public Methods *******************************************************************/
    /**
     * Parses and translates the received message from the server to local 'actions', queues the message if needed.
     * @param {Object} action - The message sent (from the server).
     */
    parseMessage(action) {
        if(action) {
            this.#actions.push(action);
            if (window.isDebugMode) {
                /* eslint-disable-next-line no-console -- This is only used when debug mode is enabled. */
                console.log(`Action "${ action.actionType }" called.`);
            }

            this.#nextAction();
        }
    }

    /**
     * Initialize & setup SignalR.
     */
    /* eslint-disable-next-line class-methods-use-this -- Cannot use/access static methods within an anonymous class expression. */
    async setupSignalR(url) {

        // Keep curent tab awake and avoid an unexpected connection closure.
        let lockResolver;
        if (window.navigator?.locks?.request) {
            const promise = new Promise((res) => {
                lockResolver = res;
            });

            window.navigator.locks.request("LeanFormsSleepLock", { mode: "shared" }, () => {
                return promise;
            });
        }

        // Create the signalR connection.
        const connection = new signalR.HubConnectionBuilder().
            withUrl(url, {
                skipNegotiation: true,
                transport: signalR.HttpTransportType.WebSockets
            })

            // SignalR.LogLevel. Trace is really chatty.
            .configureLogging(window.isDebugMode ? signalR.LogLevel.Error : signalR.LogLevel.Critical)

            // Re-tries after 5s, 15s and 50s. The last null parameter tells SignalR to stop re-trying.
            .withAutomaticReconnect([this.RETRY_05_SECONDS, this.RETRY_15_SECONDS, this.RETRY_50_SECONDS, null])
            .build();

        // Time-outs (match server side).
        connection.keepAliveIntervalInMilliseconds = this.TIMEOUT_3_MINUTES;
        connection.serverTimeoutInMilliseconds = this.TIMEOUT_6_MINUTES;

        // Connection (status) events.
        connection.onreconnecting(error => {
            if (window.isDebugMode) {
                /* eslint-disable-next-line no-console -- This is only used when debug mode is enabled. */
                if (error) {
                    console.log(`Connection lost due to error: ${error}. Reconnecting.`);
                }
                else {
                    console.log(`Connection lost. Reconnecting.`);
                }
            }
        });

        // The connectionId parameter is undefined if the HubConnection is configured to skip negotiation (that we set in the HubConnectionBuilder above).
        connection.onreconnected(connectionId => {
            if (window.isDebugMode) {
                /* eslint-disable-next-line no-console -- This is only used when debug mode is enabled. */
                console.log(`Connection reestablished. Connected with connectionId "${connectionId}".`);
            }
        });

        connection.onclose(error => {
            if (window.isDebugMode) {
                /* eslint-disable-next-line no-console -- This is only used when debug mode is enabled. */
                if (error) {
                    console.log(`Connection closed due to error: ${error}.`);
                }
                else {
                    console.log(`Connection closed.`);
                }
            }

            // Free lock.
            lockResolver();
        });

        // Start it up.
        await connection.start();

        // We're running.
        return connection;
    }
    // endregion ********************************************************************************/

    // region: Private Methods ******************************************************************/
    /**
     * Evaluate the next action in the queue.
     */
    #nextAction() {
        if (window.isDebugMode) {
            console.log(`Current actions queue length = ${this.#actions.length}.`);
        }

        if (this.#actions.length > 0) {
            const action = this.#actions.shift();

            // Any parameters?
            const params = action.messageParams;

            // Parse action.
            switch (action.actionType.toLowerCase()) {

                case "refreshantiforgerytoken":
                    this.#refreshAntiforgeryTokenMessage(params);
                    break;

                case "refreshchart":
                    this.#reloadChart(params);
                    break;

                case "refreshpage":
                    this.#reloadPage(params);
                    break;

                case "toast":
                    this.#showToast(params);
                    break;

                default:
                    break;

            }
        }
    }

    /**
     * Refresh antiforgery token message.
     * @param {Object} params - Options for controlling the operation.
     */
    #refreshAntiforgeryTokenMessage(params) {
        core.setAntiForgeryToken(params.antiforgeryToken);
    }

    /**
     * Reloads a given chart (if still open).
     * @param {Object} params - All data sent from the server.
     */
    #reloadChart(params) {
        var iFrame = application.getIframeWindow();
        var hasChart = typeof iFrame.setChartData === 'function';

        if (!hasChart) {
            return;
        }

        const chartData = params.chartData;
        const chartId = params.chartId;

        iFrame.setChartData(chartData, chartId, 'signalr');
    }

    /**
     * Reloads a given page (if still open).
     * @param {Object} params - All data sent from the server.
     */
    #reloadPage(params) {
        const url = params.url;
        const activePage = application.getIframeWindow().location.href;

        if (activePage.indexOf(url) > -1) {
            // Refresh page...
            alert(`Refresh ${url}.`);
        }
    }

    /**
     * Shows a toast message.
     * @param {Object} params                   All data sent from the server.
     */
    #showToast(params) {
        const type = params.type.toLowerCase();
        const message = params.message;

        core.notify(type, message, params);
    }
    // endregion ********************************************************************************/

}();
// endregion ************************************************************************************/
